/**
 * STEP — Tenants API
 * Typed wrappers around business registration and settings endpoints.
 */
import api from "@/lib/axios";
import type { TenantInfo } from "@/store/authStore";

export interface RegisterBusinessPayload {
  business_name: string;
  slug?: string;
  owner_email: string;
  owner_password: string;
  owner_first_name: string;
  owner_last_name?: string;
  phone?: string;
  currency?: string;
  timezone?: string;
  language?: string;
}

export interface RegisterBusinessResponse {
  tenant: TenantInfo;
  access_token: string;
  refresh_token: string;
}

export interface CheckSlugResponse {
  slug: string;
  available: boolean;
  domain: string;
}

export interface TenantBankAccount {
  bank_name?: string;
  account_number?: string;
  mfo?: string;
}

export interface UpdateTenantPayload {
  name?: string;
  phone?: string;
  currency?: string;
  timezone?: string;
  language?: string;
  settings?: Record<string, unknown>;
  // Legal requisites (owner-editable, first-class columns).
  legal_name?: string;
  tax_id?: string;
  legal_address?: string;
  physical_address?: string;
  ibt?: string;
  company_email?: string;
  bank_accounts?: TenantBankAccount[];
}

/** POST /api/v1/tenants/register/ */
export async function registerBusiness(
  payload: RegisterBusinessPayload
): Promise<RegisterBusinessResponse> {
  const res = await api.post<{ success: boolean; data: RegisterBusinessResponse }>(
    "/tenants/register/",
    payload
  );
  return res.data.data;
}

/** GET /api/v1/tenants/check-slug/?slug=xxx */
export async function checkSlug(slug: string): Promise<CheckSlugResponse> {
  const res = await api.get<{ success: boolean; data: CheckSlugResponse }>(
    "/tenants/check-slug/",
    { params: { slug } }
  );
  return res.data.data;
}

// ── Onboarding wizard ─────────────────────────────────────────────────────────

export interface ShopType {
  key: string;
  label: string;
  icon: string;
  description: string;
  modules: string[];
}

export interface OnboardingQuestion {
  key: string;
  question: string;
}

export interface WizardRegisterPayload {
  otp_ticket: string;
  business_name: string;
  slug?: string;
  business_type: string;
  answers: Record<string, boolean>;
  owner_first_name: string;
  owner_last_name?: string;
  plan_code?: string;
}

export interface WizardRegisterResult {
  access: string;
  refresh: string;
  user: import("@/store/authStore").AuthUser;
  tenant: TenantInfo;
  slug: string;
}

/** GET /api/v1/tenants/shop-types/ */
export async function getShopTypes(): Promise<ShopType[]> {
  const res = await api.get<{ success: boolean; data: ShopType[] }>("/tenants/shop-types/");
  return res.data.data;
}

/** GET /api/v1/tenants/onboarding-questions/ */
export async function getOnboardingQuestions(): Promise<OnboardingQuestion[]> {
  const res = await api.get<{ success: boolean; data: OnboardingQuestion[] }>("/tenants/onboarding-questions/");
  return res.data.data;
}

/** POST /api/v1/tenants/register/wizard/ */
export async function registerWizard(payload: WizardRegisterPayload): Promise<WizardRegisterResult> {
  const res = await api.post<{ success: boolean; data: WizardRegisterResult }>(
    "/tenants/register/wizard/",
    payload
  );
  return res.data.data;
}

/** GET /api/v1/tenants/me/ */
export async function getTenant(): Promise<TenantInfo> {
  const res = await api.get<{ success: boolean; data: TenantInfo }>("/tenants/me/");
  return res.data.data;
}

/** PATCH /api/v1/tenants/me/ */
export async function updateTenant(payload: UpdateTenantPayload): Promise<TenantInfo> {
  const res = await api.patch<{ success: boolean; data: TenantInfo }>("/tenants/me/", payload);
  return res.data.data;
}

/** PATCH /api/v1/tenants/me/ — upload logo as FormData */
export async function uploadTenantLogo(file: File): Promise<TenantInfo> {
  const formData = new FormData();
  formData.append("logo", file);
  const res = await api.patch<{ success: boolean; data: TenantInfo }>("/tenants/me/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.data;
}

export interface SubscriptionPlan {
  id: number;
  code: string;
  name: string;
  description: string;
  price_monthly: string;
  price_3month: string;
  price_6month: string;
  price_yearly: string;
  price_usd_monthly: string;
  price_usd_3month: string;
  price_usd_6month: string;
  price_usd_yearly: string;
  max_stores: number;
  max_users: number;
  max_products: number;
  features: string[];
  module_features: Record<string, boolean>;
  is_current: boolean;
  trial_days: number;
  sort_order: number;
  is_popular: boolean;
}

/** GET /api/v1/tenants/subscription/plans/ */
export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const res = await api.get<{ success: boolean; data: SubscriptionPlan[] }>(
    "/tenants/subscription/plans/"
  );
  return res.data.data;
}

export interface TenantUsage {
  stores: number;
  users: number;
  products: number;
}

/** GET /api/v1/tenants/subscription/usage/ */
export async function getTenantUsage(): Promise<TenantUsage> {
  const res = await api.get<{ success: boolean; data: TenantUsage }>(
    "/tenants/subscription/usage/"
  );
  return res.data.data;
}

/** POST /api/v1/tenants/subscription/select/ */
export async function selectSubscriptionPlan(planId: number): Promise<TenantInfo> {
  const res = await api.post<{ success: boolean; data: TenantInfo }>(
    "/tenants/subscription/select/",
    { plan_id: planId }
  );
  return res.data.data;
}

// ─── Phase 2: Billing history, cancellation, invoices ─────────────────────

export interface BillingRecord {
  id: string;
  amount: string;
  currency: string;
  payment_method: string;
  payment_method_display: string;
  plan_code: string;
  plan_name: string;
  period_start: string | null;
  period_end: string | null;
  status: "PAID" | "PENDING" | "REFUNDED";
  note: string;
  recorded_at: string;
  has_invoice: boolean;
}

/** GET /api/v1/tenants/subscription/billing/ */
export async function getTenantBillingHistory(): Promise<BillingRecord[]> {
  const res = await api.get<{ success: boolean; data: BillingRecord[] }>(
    "/tenants/subscription/billing/"
  );
  return res.data.data;
}

/**
 * Download an invoice PDF for a billing record. Uses the authenticated axios
 * instance (correct API host + bearer) and streams the blob to a file, so it
 * works from any tenant subdomain — unlike a plain <a href> which resolved to
 * the SPA host and 404'd.
 */
export async function downloadInvoice(billingRecordId: string): Promise<void> {
  const res = await api.get(
    `/tenants/subscription/billing/${billingRecordId}/invoice.pdf/`,
    { responseType: "blob" }
  );
  const blob = res.data as Blob;
  const cd = String(res.headers?.["content-disposition"] ?? "");
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `STEP-Invoice-${billingRecordId.slice(0, 8).toUpperCase()}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** POST /api/v1/tenants/subscription/cancel/ */
export async function cancelSubscription(reason?: string): Promise<TenantInfo> {
  const res = await api.post<{ success: boolean; data: TenantInfo }>(
    "/tenants/subscription/cancel/",
    { reason: reason ?? "" }
  );
  return res.data.data;
}

/** Returns a direct URL to download the invoice PDF (authenticated via header) */
export function getInvoicePdfUrl(billingRecordId: string): string {
  return `/api/v1/tenants/subscription/billing/${billingRecordId}/invoice.pdf/`;
}

// ─── Phase 4: Webhooks + Auto-renew ──────────────────────────────────────────

export interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  is_active: boolean;
  failure_count: number;
  last_failure_at: string | null;
  created_at: string;
  secret?: string; // only returned at creation
}

export interface WebhookDelivery {
  id: string;
  event_type: string;
  event_id: string;
  status: "PENDING" | "SUCCESS" | "FAILED";
  status_code: number | null;
  attempt_count: number;
  delivered_at: string | null;
  next_retry_at: string | null;
  created_at: string;
}

export const WEBHOOK_EVENT_TYPES = [
  "PLAN_CHANGE",
  "STATUS_CHANGE",
  "PAYMENT_RECORDED",
  "GRACE_PERIOD_STARTED",
  "AUTO_SUSPENDED",
  "TRIAL_EXTEND",
  "DAYS_ADDED",
  "DAYS_REDUCED",
] as const;

/** GET /api/v1/tenants/subscription/webhooks/ */
export async function getWebhookEndpoints(): Promise<WebhookEndpoint[]> {
  const res = await api.get<{ success: boolean; data: WebhookEndpoint[] }>(
    "/tenants/subscription/webhooks/"
  );
  return res.data.data;
}

/** POST /api/v1/tenants/subscription/webhooks/ */
export async function createWebhookEndpoint(payload: {
  url: string;
  events: string[];
}): Promise<WebhookEndpoint> {
  const res = await api.post<{ success: boolean; data: WebhookEndpoint }>(
    "/tenants/subscription/webhooks/",
    payload
  );
  return res.data.data;
}

/** DELETE /api/v1/tenants/subscription/webhooks/:id/ */
export async function deleteWebhookEndpoint(endpointId: string): Promise<void> {
  await api.delete(`/tenants/subscription/webhooks/${endpointId}/`);
}

/** GET /api/v1/tenants/subscription/webhooks/:id/deliveries/ */
export async function getWebhookDeliveries(endpointId: string): Promise<WebhookDelivery[]> {
  const res = await api.get<{ success: boolean; data: WebhookDelivery[] }>(
    `/tenants/subscription/webhooks/${endpointId}/deliveries/`
  );
  return res.data.data;
}

/** PATCH /api/v1/tenants/subscription/auto-renew/ */
export async function setAutoRenew(autoRenew: boolean): Promise<{ auto_renew: boolean }> {
  const res = await api.patch<{ success: boolean; data: { auto_renew: boolean } }>(
    "/tenants/subscription/auto-renew/",
    { auto_renew: autoRenew }
  );
  return res.data.data;
}

// ─── Phase 5: Custom Domains (Option D — Enterprise only) ─────────────────────

export interface CustomDomain {
  id: number;
  domain: string;
  status: "unverified" | "verified" | "ssl_issued" | "failed";
  is_primary: boolean;
  created_at: string;
  verified_at: string | null;
  ssl_issued_at: string | null;
  failure_reason: string | null;
  instructions: string | null;
}

/** GET /api/v1/tenants/custom-domains/ */
export async function getCustomDomains(): Promise<CustomDomain[]> {
  const res = await api.get<{ success: boolean; data: CustomDomain[] }>("/tenants/custom-domains/");
  return res.data.data;
}

/** POST /api/v1/tenants/custom-domains/ */
export async function addCustomDomain(domain: string): Promise<CustomDomain> {
  const res = await api.post<{ success: boolean; data: CustomDomain }>("/tenants/custom-domains/", { domain });
  return res.data.data;
}

/** POST /api/v1/tenants/custom-domains/{id}/verify/ */
export async function verifyCustomDomain(id: number): Promise<CustomDomain> {
  const res = await api.post<{ success: boolean; data: CustomDomain }>(`/tenants/custom-domains/${id}/verify/`);
  return res.data.data;
}

/** DELETE /api/v1/tenants/custom-domains/{id}/ */
export async function deleteCustomDomain(id: number): Promise<void> {
  await api.delete(`/tenants/custom-domains/${id}/`);
}
